import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiCall } from '../../config/api';
import { hashPassword } from '../../utils/crypto';
import { Eye, EyeOff } from 'lucide-react';
import Footer from '../common/Footer';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Decode email from URL token parameter
    const token = searchParams.get('token');
    if (token) {
      try {
        const decodedEmail = atob(token); // Base64 decode
        setEmail(decodedEmail);
        console.log('Email decoded from token:', decodedEmail);
      } catch (error) {
        console.error('Invalid token:', error);
        setMessage({ type: 'error', text: 'Invalid or expired reset link' });
      }
    } else {
      setMessage({ type: 'error', text: 'No reset token found in URL' });
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ type: '', text: '' });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage({ type: 'error', text: 'Invalid reset link' });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (formData.password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const hashedPwd = await hashPassword(formData.password);
      const response = await apiCall('/users/forget-pass', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({
          identifier: email,
          password: hashedPwd
        })
      });
      if (response.ok) {
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.message || 'Failed to reset password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
      console.error('Reset password error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden animate-fadeIn">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/images/Map-2.png')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-gray-900/30 to-blue-800/40 dark:from-gray-900/60 dark:via-gray-800/50 dark:to-gray-900/60"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Reset Password Card - Dark Design */}
        <div className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 animate-scaleIn">
          {/* Logo */}
          <div className="text-center mb-4 sm:mb-6 md:mb-8 animate-slideDown">
            <div className="inline-flex items-center justify-center mb-3 sm:mb-4">
              <img 
                src="/images/logo.png" 
                alt="QuickLook Logo" 
                className="h-28 sm:h-32 md:h-40 w-auto drop-shadow-2xl"
              />
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
              Create New Password
            </h2>
          </div>

          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-300 hover:text-white mb-6"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Login
          </button>

          {/* Message Display */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-xl ${
              message.type === 'success' 
                ? 'bg-green-500/20 border border-green-400 text-green-200' 
                : 'bg-red-500/20 border border-red-400 text-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {email && (
            <form onSubmit={handleResetPassword} className="space-y-4 sm:space-y-5 md:space-y-6 relative">
              {/* Loading Bar */}
              {loading && (
                <div className="absolute -top-4 left-0 right-0 h-1 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 animate-[loadingBar_2s_linear]" style={{animation: 'loadingBar 2s linear', animationFillMode: 'forwards'}} />
                </div>
              )}
              <style>{`
                @keyframes loadingBar {
                  from { width: 0%; }
                  to { width: 100%; }
                }
              `}</style>
              {/* Email Display (Read-only) */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-200 mb-1.5 sm:mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  className="w-full px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base border-0 rounded-2xl bg-gray-600 text-gray-300 cursor-not-allowed"
                  disabled
                />
              </div>

              {/* New Password Input */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-200 mb-1.5 sm:mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base border-0 rounded-2xl bg-slate-800/80 text-white placeholder-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none focus:bg-slate-800/80"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-300">
                  Password must be at least 6 characters long
                </p>
              </div>

              {/* Confirm Password Input */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-200 mb-1.5 sm:mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base border-0 rounded-2xl bg-slate-800/80 text-white placeholder-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none focus:bg-slate-800/80"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold text-lg py-4 rounded-2xl transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Resetting Password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}

          <Footer tone="dark" className="mt-6" />
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
